import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { cn } from '../lib/utils';

const CITY_PAGE_SIZE = 40;
const CITY_SEARCH_DEBOUNCE_MS = 350;

/**
 * Shared State → City → (optional) Pincode fields backed by /api/master/*.
 * - Pincode autofills state + city (keeps city if valid for PIN)
 * - City autofills pincode only when that city has exactly one PIN
 */
export default function LocationFields({
  value,
  onChange,
  showPincode = false,
  showCountry = false,
  country = 'India',
  variant = 'wizard', // 'wizard' | 'admin'
  errors = {},
}) {
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityOpen, setCityOpen] = useState(false);
  const [cityQuery, setCityQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [cityOffset, setCityOffset] = useState(0);
  const [cityHasMore, setCityHasMore] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesLoadingMore, setCitiesLoadingMore] = useState(false);
  const [pincodeError, setPincodeError] = useState('');
  const [lookingUpPin, setLookingUpPin] = useState(false);
  const [resolvingCityPin, setResolvingCityPin] = useState(false);
  const lastLookedUpPin = useRef('');
  const listRef = useRef(null);
  const loadGen = useRef(0);

  const stateName = value?.state || '';
  const cityName = value?.city || '';
  const pincode = value?.pincode || '';
  const stateId = value?.state_id || '';

  const selectClass =
    variant === 'wizard'
      ? 'rounded-xl border-slate-200'
      : 'h-10 rounded-lg border-[#E2E8F0] text-[13px] text-[#334155]';
  const inputClass =
    variant === 'wizard'
      ? 'rounded-xl border-slate-200'
      : 'h-10 rounded-lg border-[#E2E8F0] text-[13px]';
  const cityTriggerClass =
    variant === 'admin'
      ? 'bg-white text-[#334155] hover:bg-slate-50 hover:text-[#334155] focus-visible:ring-blue-500/20'
      : 'bg-white text-slate-900 hover:bg-slate-50 hover:text-slate-900';
  const cityPlaceholderClass =
    variant === 'admin' ? 'text-slate-400' : 'text-muted-foreground';
  const cityItemClass =
    'cursor-pointer data-[selected=true]:bg-slate-100 data-[selected=true]:text-slate-900';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/master/states');
        if (!cancelled) setStates(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setStates([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Debounce city search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(cityQuery), CITY_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [cityQuery]);

  // Resolve state_id from name when editing existing records
  useEffect(() => {
    if (stateId || !stateName || !states.length) return;
    const match = states.find((s) => s.name.toLowerCase() === stateName.toLowerCase());
    if (match) {
      onChange({ ...value, state: match.name, state_id: match.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states, stateName, stateId]);

  const fetchCitiesPage = useCallback(async (sid, q, offset, append) => {
    if (!sid) {
      setCities([]);
      setCityHasMore(false);
      return;
    }
    const gen = ++loadGen.current;
    if (append) setCitiesLoadingMore(true);
    else {
      setCitiesLoading(true);
      setCityHasMore(false);
    }
    try {
      const params = { state_id: sid, limit: CITY_PAGE_SIZE, offset };
      if (q && q.trim()) params.q = q.trim();
      const { data } = await api.get('/api/master/cities', { params });
      if (gen !== loadGen.current) return;

      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      const hasMore = Boolean(data?.has_more) && items.length > 0;
      setCities((prev) => (append ? [...prev, ...items] : items));
      setCityOffset(offset + items.length);
      setCityHasMore(hasMore);
    } catch {
      if (gen !== loadGen.current) return;
      if (!append) setCities([]);
      setCityHasMore(false);
    } finally {
      if (gen === loadGen.current) {
        setCitiesLoading(false);
        setCitiesLoadingMore(false);
      }
    }
  }, []);

  // Reset + load first page when state or debounced query changes
  useEffect(() => {
    if (!stateId) {
      setCities([]);
      setCityHasMore(false);
      setCityOffset(0);
      return;
    }
    setCityOffset(0);
    fetchCitiesPage(stateId, debouncedQuery, 0, false);
  }, [stateId, debouncedQuery, fetchCitiesPage]);

  const handleCityListScroll = (e) => {
    const el = e.currentTarget;
    if (!cityHasMore || citiesLoadingMore || citiesLoading || cities.length === 0) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
    if (nearBottom) {
      fetchCitiesPage(stateId, debouncedQuery, cityOffset, true);
    }
  };

  const handleStateChange = (id) => {
    const st = states.find((s) => s.id === id);
    onChange({
      ...value,
      state: st?.name || '',
      state_id: id,
      city: '',
      city_id: '',
      pincode: showPincode ? '' : value.pincode,
    });
    setCityQuery('');
    setDebouncedQuery('');
    setPincodeError('');
    lastLookedUpPin.current = '';
  };

  const handleCitySelect = async (city) => {
    const next = {
      ...value,
      city: city.name,
      city_id: city.id,
    };
    onChange(next);
    setCityOpen(false);
    setCityQuery('');
    setPincodeError('');

    if (!showPincode) return;

    // Autofill pincode only when this city maps to exactly one PIN
    // (many localities share multiple PINs / same name — avoid wrong PIN)
    setResolvingCityPin(true);
    try {
      const { data } = await api.get(`/api/master/cities/${city.id}/pincodes`);
      const pins = Array.isArray(data?.pincodes) ? data.pincodes : [];
      if (pins.length === 1) {
        lastLookedUpPin.current = pins[0];
        onChange({ ...next, pincode: pins[0] });
      } else if (pins.length > 1) {
        // Keep existing pincode if it still belongs to this city; else clear
        const current = (value.pincode || '').trim();
        if (current && pins.includes(current)) {
          onChange({ ...next, pincode: current });
        } else {
          onChange({ ...next, pincode: '' });
        }
      } else {
        onChange({ ...next, pincode: '' });
      }
    } catch {
      // leave pincode for user to enter
    } finally {
      setResolvingCityPin(false);
    }
  };

  const handlePincodeChange = async (raw) => {
    const digits = raw.replace(/\D/g, '').slice(0, 6);
    onChange({ ...value, pincode: digits });
    setPincodeError('');

    if (digits.length !== 6) {
      lastLookedUpPin.current = '';
      return;
    }
    if (digits === lastLookedUpPin.current) return;
    lastLookedUpPin.current = digits;

    setLookingUpPin(true);
    try {
      const { data } = await api.get(`/api/master/pincodes/${digits}`);
      const localities = Array.isArray(data.cities) ? data.cities : [];
      const currentName = (value.city || '').trim().toLowerCase();
      const currentId = value.city_id || '';

      const matched =
        localities.find((c) => c.id && c.id === currentId) ||
        localities.find((c) => (c.name || '').toLowerCase() === currentName);

      const nextCity = matched
        ? { city: matched.name, city_id: matched.id }
        : { city: data.city || '', city_id: data.city_id || '' };

      onChange({
        ...value,
        pincode: digits,
        state: data.state || '',
        state_id: data.state_id || '',
        ...nextCity,
      });
      setPincodeError('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setPincodeError(typeof detail === 'string' ? detail : 'Pincode not found');
    } finally {
      setLookingUpPin(false);
    }
  };

  return (
    <>
      <div>
        {variant === 'admin' && (
          <span className="block text-[12px] font-semibold text-[#334155] mb-1.5">
            State
          </span>
        )}
        <Select value={stateId || undefined} onValueChange={handleStateChange}>
          <SelectTrigger className={cn(selectClass, errors.state && 'border-red-400')}>
            <SelectValue placeholder={variant === 'admin' ? 'Select state' : 'State *'} />
          </SelectTrigger>
          <SelectContent
            position="popper"
            side="bottom"
            sideOffset={4}
            collisionPadding={16}
            className="max-h-72 z-[80]"
          >
            {states.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.state && <p className="text-[11px] text-red-500 mt-1">{errors.state}</p>}
      </div>

      <div>
        {variant === 'admin' && (
          <span className="block text-[12px] font-semibold text-[#334155] mb-1.5">
            City
          </span>
        )}
        <Popover open={cityOpen} onOpenChange={setCityOpen} modal>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              disabled={!stateId}
              className={cn(
                'w-full justify-between font-normal shadow-none',
                selectClass,
                cityTriggerClass,
                !cityName && cityPlaceholderClass,
                errors.city && 'border-red-400'
              )}
            >
              <span className={cn('truncate', !cityName && cityPlaceholderClass)}>
                {cityName || (stateId ? (variant === 'admin' ? 'Select city' : 'City *') : 'Select state first')}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 text-slate-500" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0 z-[100]"
            align="start"
            side="bottom"
            sideOffset={4}
            collisionPadding={16}
            avoidCollisions
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <Command shouldFilter={false} className="overflow-hidden">
              <CommandInput
                placeholder="Search city..."
                value={cityQuery}
                onValueChange={setCityQuery}
                className="text-[13px]"
              />
              <CommandList
                ref={listRef}
                className="max-h-60 overflow-y-scroll overscroll-contain"
                onScroll={handleCityListScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                {citiesLoading && cities.length === 0 ? (
                  <div className="py-6 flex justify-center text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  <>
                    <CommandEmpty>No city found.</CommandEmpty>
                    <CommandGroup className="overflow-visible">
                      {cities.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={c.name}
                          onSelect={() => handleCitySelect(c)}
                          className={cityItemClass}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4 shrink-0 text-slate-700',
                              cityName === c.name ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <span className="truncate">{c.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    {citiesLoadingMore && (
                      <div className="py-2 flex justify-center text-slate-400">
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      </div>
                    )}
                    {!citiesLoadingMore && cityHasMore && (
                      <p className="px-2 py-1.5 text-[10px] text-center text-slate-400">
                        Scroll for more…
                      </p>
                    )}
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {errors.city && <p className="text-[11px] text-red-500 mt-1">{errors.city}</p>}
      </div>

      {showPincode && (
        <div>
          <Input
            value={pincode}
            onChange={(e) => handlePincodeChange(e.target.value)}
            placeholder={
              lookingUpPin || resolvingCityPin ? 'Looking up…' : 'Pincode *'
            }
            maxLength={6}
            inputMode="numeric"
            className={cn(inputClass, (errors.pincode || pincodeError) && 'border-red-400')}
          />
          {(pincodeError || errors.pincode) && (
            <p className="text-[11px] text-red-500 mt-1">{pincodeError || errors.pincode}</p>
          )}
        </div>
      )}

      {showCountry && (
        <Input value={country} disabled className="rounded-xl bg-slate-50/80 border-slate-200" />
      )}
    </>
  );
}

LocationFields.propTypes = {
  value: PropTypes.shape({
    state: PropTypes.string,
    city: PropTypes.string,
    pincode: PropTypes.string,
    state_id: PropTypes.string,
    city_id: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  showPincode: PropTypes.bool,
  showCountry: PropTypes.bool,
  country: PropTypes.string,
  variant: PropTypes.oneOf(['wizard', 'admin']),
  errors: PropTypes.object,
};
